import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  compterParTranche,
  format,
  money,
  tranches as decouper,
  type CurrencyCode,
  type Pas,
  type Tranche,
} from '@iobiz/shared'
import { useJoursCreationClients, useSerieVentes, useSortiesSerie } from '../../data/hooks.js'
import type { CoutsArticles } from '../../data/repositories/sales.local.js'
import { GrapheColonnes, type SerieGraphe } from '../../ui/charts/GrapheColonnes.js'
import { GrapheStock, type ArticleEnAlerte } from '../../ui/charts/GrapheStock.js'
import { LienPrixAchat } from './LienPrixAchat.js'
import { compact, libellesDe } from './tranches.js'

/**
 * Le graphe d'une carte du tableau de bord.
 *
 * ⭐ Chaque carte ouvre SON indicateur, à SA granularité : « CA du jour »
 * s'ouvre sur les trente derniers jours, « CA du mois » sur les douze derniers
 * mois. La dernière colonne vaut le chiffre de la carte ; le sélecteur permet
 * ensuite de changer d'horizon sans refermer.
 *
 * ⭐ Une vue TABLEAU double chaque graphe : aucune valeur n'est accessible au
 * seul survol, et un lecteur d'écran lit un tableau bien mieux qu'un dessin.
 */

export type Indicateur = 'ca' | 'depenses' | 'ventes' | 'panier' | 'clients' | 'alerte'

const TITRES: Record<Indicateur, string> = {
  ca: 'Chiffre d’affaires et bénéfice net',
  depenses: 'Dépenses',
  ventes: 'Nombre de ventes',
  panier: 'Panier moyen',
  clients: 'Nouveaux clients',
  alerte: 'Produits en alerte',
}

const HORIZONS: { pas: Pas; libelle: string }[] = [
  { pas: 'jour', libelle: '30 jours' },
  { pas: 'mois', libelle: '12 mois' },
  { pas: 'annee', libelle: 'Par année' },
]


export function GrapheDialog({
  open,
  onClose,
  indicateur,
  pasInitial,
  storeId,
  devise,
  aujourdhui,
  articlesEnAlerte,
  onCompleterPrixAchat,
  coutsAbsents,
}: {
  open: boolean
  onClose: () => void
  indicateur: Indicateur
  pasInitial: Pas
  storeId: string | undefined
  devise: CurrencyCode
  aujourdhui: string
  articlesEnAlerte: readonly ArticleEnAlerte[]
  /** Ferme le graphe et ouvre les produits sans prix d'achat. */
  onCompleterPrixAchat: () => void
  /** Prix d'achat des articles supprimés, fournis par le serveur. */
  coutsAbsents: CoutsArticles
}): JSX.Element {
  const [pas, setPas] = useState<Pas>(pasInitial)
  const [vue, setVue] = useState<'graphe' | 'tableau'>('graphe')
  /** L'horizon a-t-il été choisi à la main ? Alors on ne le change plus. */
  const [choixManuel, setChoixManuel] = useState(false)
  const [basculeAuto, setBasculeAuto] = useState(false)
  useEffect(() => {
    if (!open) return
    setPas(pasInitial)
    setChoixManuel(false)
    setBasculeAuto(false)
  }, [open, pasInitial])

  const serie = useSerieVentes(open ? storeId : undefined, pas, aujourdhui, coutsAbsents)
  const joursClients = useJoursCreationClients(
    open && indicateur === 'clients' ? storeId : undefined,
  )

  /**
   * Les tranches du graphe.
   *
   * ⚠️ Pour les clients au pas annuel, on remonte à la plus ancienne FICHE et
   * non à la première vente : un fichier importé précède souvent la première
   * facture, et ses années disparaîtraient du graphe.
   */
  const decoupage = useMemo<Tranche[]>(() => {
    if (indicateur === 'clients' && pas === 'annee' && joursClients?.length) {
      const premiere = joursClients.reduce((a, b) => (b < a ? b : a))
      return decouper('annee', aujourdhui, Number(premiere.slice(0, 4)))
    }
    return serie?.tranches ?? decouper(pas, aujourdhui)
  }, [indicateur, pas, aujourdhui, serie, joursClients])

  /**
   * ⚠️ Une seule année d'activité donnerait un graphe à UNE colonne, qui ne
   * compare rien. On ouvre alors les douze derniers mois, et on le dit — sans
   * jamais passer outre un choix fait à la main.
   */
  const donneesPretes = serie !== undefined && (indicateur !== 'clients' || joursClients !== undefined)
  useEffect(() => {
    if (!choixManuel && pas === 'annee' && donneesPretes && decoupage.length < 2) {
      setPas('mois')
      setBasculeAuto(true)
    }
  }, [choixManuel, pas, donneesPretes, decoupage.length])

  /**
   * L'horizon RÉELLEMENT tracé, lu sur les tranches elles-mêmes.
   *
   * ⚠️ Pas `pas` : au changement d'horizon, la série précédente reste affichée
   * le temps que la nouvelle arrive. Libellés, dépenses et comptes doivent
   * suivre les tranches qu'on dessine, sinon des mois s'étiquettent en jours.
   */
  const pasTrace: Pas =
    decoupage[0]?.cle.length === 10 ? 'jour' : decoupage[0]?.cle.length === 7 ? 'mois' : 'annee'

  // Les sorties viennent du SERVEUR : elles ne sont pas répliquées.
  const besoinSorties = indicateur === 'ca' || indicateur === 'depenses'
  const { sorties, enCours: sortiesEnCours } = useSortiesSerie(
    storeId,
    decoupage[0]?.du,
    aujourdhui,
    pasTrace,
    open && besoinSorties,
  )

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const nombre = (v: number): string => new Intl.NumberFormat('fr-FR').format(v)
  const { courts, longs } = libellesDe(decoupage, pasTrace)

  const parCle = new Map(sorties?.map((s) => [s.cle, s]) ?? [])
  const points = new Map(serie?.points.map((p) => [p.cle, p]) ?? [])
  const point = (cle: string) => points.get(cle)

  /** Ce que le graphe trace, selon la carte qui l'a ouvert. */
  const trace = ((): {
    colonnes: SerieGraphe[]
    ligne?: SerieGraphe
    formater: (v: number) => string
    entiers?: boolean
  } => {
    switch (indicateur) {
      case 'ca':
        return {
          colonnes: [{ nom: 'Chiffre d’affaires', valeurs: decoupage.map((t) => point(t.cle)?.ca ?? 0) }],
          // Sans les dépenses (hors ligne), pas de bénéfice : le tracer sans
          // le loyer serait un chiffre faux présenté comme juste.
          ...(sorties
            ? {
                ligne: {
                  nom: 'Bénéfice net',
                  valeurs: decoupage.map((t) => {
                    const p = point(t.cle)
                    return (
                      (p?.ventesHt ?? 0) - (p?.coutAchat ?? 0) - (parCle.get(t.cle)?.courantes ?? 0)
                    )
                  }),
                },
              }
            : {}),
          formater: prix,
        }
      case 'depenses':
        return {
          colonnes: [
            { nom: 'Produits', valeurs: decoupage.map((t) => parCle.get(t.cle)?.produits ?? 0) },
            {
              nom: 'Dépenses courantes',
              valeurs: decoupage.map((t) => parCle.get(t.cle)?.courantes ?? 0),
            },
          ],
          formater: prix,
        }
      case 'ventes':
        return {
          colonnes: [{ nom: 'Ventes', valeurs: decoupage.map((t) => point(t.cle)?.ventes ?? 0) }],
          formater: nombre,
          entiers: true,
        }
      case 'panier':
        return {
          colonnes: [
            { nom: 'Panier moyen', valeurs: decoupage.map((t) => point(t.cle)?.panierMoyen ?? null) },
          ],
          formater: prix,
        }
      case 'clients': {
        const comptes = compterParTranche(joursClients ?? [], pasTrace, decoupage)
        return {
          colonnes: [{ nom: 'Nouveaux clients', valeurs: comptes }],
          formater: nombre,
          entiers: true,
        }
      }
      default:
        return { colonnes: [], formater: nombre }
    }
  })()

  // Seulement quand le bénéfice est tracé — il faut pour cela les dépenses.
  const sansPrixAchat =
    indicateur === 'ca' && sorties ? decoupage.reduce((s, t) => s + (point(t.cle)?.lignesSansPrixAchat ?? 0), 0) : 0
  const sortiesIndisponibles = besoinSorties && !sorties && !sortiesEnCours
  const seriesTableau = [...trace.colonnes, ...(trace.ligne ? [trace.ligne] : [])]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{TITRES[indicateur]}</DialogTitle>
      <DialogContent>
        {indicateur === 'alerte' ? (
          <GrapheStock articles={articlesEnAlerte} />
        ) : (
          <>
            {/* Les réglages sur UNE ligne, au-dessus de ce qu'ils règlent. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'space-between',
                mb: 4,
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={pas}
                onChange={(_, v: Pas | null) => {
                  if (!v) return
                  setPas(v)
                  setChoixManuel(true)
                  setBasculeAuto(false)
                }}
                aria-label="Horizon du graphe"
              >
                {HORIZONS.map((h) => (
                  <ToggleButton key={h.pas} value={h.pas}>
                    {h.libelle}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={vue}
                onChange={(_, v: 'graphe' | 'tableau' | null) => v && setVue(v)}
                aria-label="Présentation"
              >
                <ToggleButton value="graphe">Graphe</ToggleButton>
                <ToggleButton value="tableau">Tableau</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {basculeAuto ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                Une seule année d’activité pour l’instant : affichage sur les douze derniers mois.
              </Typography>
            ) : null}

            {sortiesIndisponibles ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {indicateur === 'ca'
                  ? 'Dépenses indisponibles hors ligne : le bénéfice net n’est pas tracé.'
                  : 'Dépenses indisponibles hors ligne.'}
              </Alert>
            ) : null}

            {/* ⭐ Au changement d'horizon, le graphe précédent reste affiché,
                estompé : pas de squelette, pas de saut de mise en page. */}
            <Box sx={{ opacity: !serie || sortiesEnCours ? 0.5 : 1, transition: 'none' }}>
              {vue === 'graphe' ? (
                <GrapheColonnes
                  titre={TITRES[indicateur]}
                  libelles={courts}
                  libellesLongs={longs}
                  colonnes={trace.colonnes}
                  ligne={trace.ligne}
                  formater={trace.formater}
                  formaterAxe={(v) => compact.format(v)}
                  entiers={trace.entiers}
                />
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Période</TableCell>
                        {seriesTableau.map((s) => (
                          <TableCell key={s.nom} align="right">
                            {s.nom}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Du plus récent au plus ancien : c'est la période en
                          cours qu'on vient chercher. */}
                      {decoupage
                        .map((t, i) => ({ t, i }))
                        .reverse()
                        .map(({ t, i }) => (
                          <TableRow key={t.cle}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{longs[i]}</TableCell>
                            {seriesTableau.map((s) => {
                              const v = s.valeurs[i]
                              return (
                                <TableCell
                                  key={s.nom}
                                  align="right"
                                  sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                                >
                                  {v === null || v === undefined ? '—' : trace.formater(v)}
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>

            {sansPrixAchat > 0 ? (
              <Box sx={{ mt: 2 }}>
                <LienPrixAchat nombre={sansPrixAchat} onClick={onCompleterPrixAchat} />
              </Box>
            ) : null}
            {indicateur === 'depenses' ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
                « Produits » : marchandise entrée en stock, au prix d’achat.
              </Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
